import { displayNameInRightFormat } from "../../../utils/helpers";
import {
    ApplicableFeeSource,
    ClientFeeValues,
    ClientTagKey,
    ICollectionsNoteUser,
    IClientFee,
    RingCentralDestination,
} from "../types/clients.types";

export const CLIENT_TAG_DETAILS: Record<
    ClientTagKey,
    { label: string; description: string }
> = {
    [ClientTagKey.READY_TO_COMMIT]: {
        label: "Ready to Commit",
        description: "The person has booked or is ready to start care.",
    },
    [ClientTagKey.LOW_BARRIER_NOT_BOOKING]: {
        label: "Low Barrier, Not Booking",
        description: "The person has a route to start care but has not booked.",
    },
    [ClientTagKey.ON_THE_FENCE]: {
        label: "On the Fence",
        description:
            "The person is interested in care but is still deciding whether or how to start.",
    },
    [ClientTagKey.NOT_YET]: {
        label: "Not Yet",
        description:
            "The person is not proceeding now but may benefit from a relevant future recheck.",
    },
    [ClientTagKey.STOPPED_RESPONDING]: {
        label: "Stopped Responding",
        description:
            "The person completed an inquiry but has stopped responding to follow-up.",
    },
    [ClientTagKey.WAITLIST]: {
        label: "Waitlist",
        description:
            "The person is waiting for a suitable therapist or available slot.",
    },
    [ClientTagKey.INCOMPLETE_INQUIRY]: {
        label: "Incomplete Inquiry",
        description: "The person started an inquiry but did not complete it.",
    },
    [ClientTagKey.INSURANCE_TIER_1]: {
        label: "Insurance Tier 1",
        description:
            "NYSHIP/Carelon or NYCE PPO, as confirmed by a benefits review.",
    },
    [ClientTagKey.INSURANCE_TIER_2]: {
        label: "Insurance Tier 2",
        description:
            "Other plans with a deductible under $3,000 or reimbursement of at least " +
            "90% UCR or 300% Medicare, as confirmed by a benefits review.",
    },
    [ClientTagKey.INSURANCE_TIER_3]: {
        label: "Insurance Tier 3",
        description:
            "High-deductible and other TBD plans with a deductible of $3,000 or more, " +
            "as confirmed by a benefits review.",
    },
};

export const CLIENT_TAG_KEYS = Object.values(ClientTagKey);

export function getCollectionsNoteUserName(user: ICollectionsNoteUser | null) {
    if (!user) return "--";

    return displayNameInRightFormat({
        firstName: user.first_name,
        lastName: user.last_name,
    });
}

export function getProviderApplicableFeesFromFee(fee: IClientFee) {
    const seen = new Set<string>();

    return fee.providers.reduce<typeof fee.providers>((acc, provider) => {
        if (!provider.applicable_fee_value) return acc;

        const key = `${provider.provider_id}:${provider.service}:${
            provider.applicable_fee_source
        }:${provider.applicable_fee_type ?? ""}`;

        if (seen.has(key)) return acc;
        seen.add(key);

        acc.push(provider);
        return acc;
    }, []);
}

export function getFeesToHighlightFromFee(fee: IClientFee): ClientFeeValues[] {
    const providerFees = getProviderApplicableFeesFromFee(fee);
    const applicableTypes = new Set<ClientFeeValues>();

    providerFees.forEach((provider) => {
        if (
            provider.applicable_fee_type &&
            provider.applicable_fee_source === ApplicableFeeSource.GENERAL
        ) {
            applicableTypes.add(provider.applicable_fee_type);
        }
    });

    return [...applicableTypes];
}

export const getMemoListNavigateUrl = () => {
    // Get note list filters from localstorage
    const memoListFiltersFromLS = localStorage.getItem("memolist_filters");

    if (memoListFiltersFromLS) {
        return `${memoListFiltersFromLS}`;
    }
    return "?tab=memo";
};

export const getClientClaimsListNavigateUrl = () => {
    // Get client claims list filters from localstorage
    const clientClaimsListFiltersFromLS = localStorage.getItem(
        "clientclaimslist_filters"
    );

    if (clientClaimsListFiltersFromLS) {
        return `${clientClaimsListFiltersFromLS}`;
    }
    return "?tab=claims";
};

export const FLAG_COLORS = [
    { name: "Blue", value: "#3B82F6" },
    { name: "Green", value: "#10B981" },
    { name: "Orange", value: "#F59E0B" },
    { name: "Red", value: "#EF4444" },
    { name: "Purple", value: "#8B5CF6" },
    { name: "Pink", value: "#EC4899" },
    { name: "Yellow", value: "#FFD600" },
    { name: "Brown", value: "#8D5524" },
    { name: "Slate", value: "#64748B" },
    { name: "Lime", value: "#A3E635" },
    { name: "Magenta", value: "#D72660" },
    { name: "Turquoise", value: "#1DE9B6" },
];

export const DEFAULT_FLAG_COLOR = "#3B82F6";

export const FLAG_CONTENT_DELIMITER = "⟨◊⟩";

export const getClientFlagDetails = (content?: string) => {
    if (!content) return null;

    const parts = content.split(FLAG_CONTENT_DELIMITER);

    if (parts.length === 2) {
        return {
            label: parts[0] ?? content,
            color: parts[1] ?? DEFAULT_FLAG_COLOR,
        };
    }

    return {
        label: content,
        color: DEFAULT_FLAG_COLOR,
    };
};

export const createFlagContent = (label: string, color: string) => {
    return `${label}${FLAG_CONTENT_DELIMITER}${color}`;
};

export function formatCallDuration(durationInSeconds: number | null) {
    if (durationInSeconds === null || durationInSeconds < 0) return "--";
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = durationInSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

export function getRingCentralUrl(
    value: string,
    destination: RingCentralDestination
): string | null {
    const digits = value.replace(/\D/g, "");
    const dialableDigits = digits.length === 10 ? `1${digits}` : digits;

    if (dialableDigits.length !== 11 || !dialableDigits.startsWith("1")) {
        return null;
    }

    const destinations: Record<RingCentralDestination, string> = {
        dialer: `/dialer?number=${dialableDigits}`,
        "new-sms": `/sms?type=new&number=${dialableDigits}`,
        "call-history": `/history?number=${dialableDigits}`,
        "sms-history": `/sms?number=${dialableDigits}`,
    };

    return `https://app.ringcentral.com/r${destinations[destination]}`;
}
